import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { ChevronDown, ChevronUp, Layers, Upload } from "lucide-react";
import { api } from "@/api/client";
import AlbumTree from "@/components/media/AlbumTree";
import MediaUploadArea from "@/components/media/MediaUploadArea";
import MediaDetailDrawer from "@/components/media/MediaDetailDrawer";
import MergeDuplicatesDialog from "@/components/media/MergeDuplicatesDialog";
import MediaFilters, { MediaFolderFilter } from "@/components/media/MediaFilters";
import MediaGrid from "@/components/media/MediaGrid";
import MediaBulkBar from "@/components/media/MediaBulkBar";
import PhotoViewer from "@/components/media/PhotoViewer";
import { useMediaSelection } from "@/components/media/useMediaSelection";
import type { TileClickIntent } from "@/components/media/MediaTile";
import { MEDIA_LIST_KEY, MEDIA_SOURCES_KEY, useMediaSources } from "@/hooks/useMediaSources";
import {
  MEDIA_DETAIL_PARAM,
  activeMediaFilterCount,
  clearMediaFilters,
  mediaGridState,
  parseMediaFilters,
  setMediaDetail,
  setMediaFilter,
  useMediaLibrary,
  type MediaFilterKey,
} from "@/hooks/useMediaLibrary";

/**
 * Stránka Média (vize podnety-tymu-2026-10, F7.5–F7.7): filtry v adrese, mřížka s celkovým
 * počtem a donačítáním posunem, výběr (lišta `MediaBulkBar`), velký prohlížeč a detail fotky.
 *
 * Pravidlo kliku: prostý klik na dlaždici otevře prohlížeč (v koši detail, protože prohlížeč
 * nabízí ★ a smazání do koše), zaškrtávátko a Cmd/Ctrl-klik přepnou výběr, Shift-klik
 * vybere rozsah.
 */
export default function MediaPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = parseMediaFilters(searchParams);
  const filtersKey = JSON.stringify(filters);
  const detailId = searchParams.get(MEDIA_DETAIL_PARAM);
  const trashMode = !!filters.trash;

  const [uploadOpen, setUploadOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  const library = useMediaLibrary(filters);
  const { items } = library;
  const sources = useMediaSources();
  const selection = useMediaSelection();
  const grid = mediaGridState({
    isLoading: library.isLoading,
    isError: library.isError,
    isFetchNextPageError: library.isFetchNextPageError,
    itemsLength: items.length,
  });
  const activeCount = activeMediaFilterCount(filters);

  // Výběr se ruší při změně filtrů, koše a alba (ne při otevření detailu). Objekt
  // `selection` se mění s každou změnou výběru, v závislostech je jen stabilní `clear`.
  const { clear } = selection;
  useEffect(() => {
    clear();
  }, [filtersKey, clear]);

  // Prohlížeč patří k jednomu seznamu; po změně filtrů se zavře (indexy by ukazovaly jinam).
  const [viewerFiltersKey, setViewerFiltersKey] = useState(filtersKey);
  if (viewerFiltersKey !== filtersKey) {
    setViewerFiltersKey(filtersKey);
    setViewerIndex(null);
  }
  // Seznam se zkrátil pod index jinak než smazáním z prohlížeče (obnova po uložení detailu
  // fotku vyřadila z filtru, další stránka přišla prázdná): index se ořízne do rozsahu,
  // jinak by se prohlížeč skryl a po donačtení sám otevřel nad cizí fotkou.
  if (viewerIndex !== null && viewerIndex >= items.length) {
    setViewerIndex(items.length > 0 ? items.length - 1 : null);
  }

  const setFilter = useCallback(
    (key: MediaFilterKey, value: string | null) =>
      setSearchParams((prev) => setMediaFilter(prev, key, value), { replace: true }),
    [setSearchParams],
  );
  const clearFilters = useCallback(
    () => setSearchParams(clearMediaFilters(), { replace: true }),
    [setSearchParams],
  );
  const setDetail = useCallback(
    (id: string | null) => setSearchParams((prev) => setMediaDetail(prev, id), { replace: true }),
    [setSearchParams],
  );

  const invalidateList = () => queryClient.invalidateQueries({ queryKey: MEDIA_LIST_KEY });
  // Nabídka zdrojů = zdroje ŽIVÝCH fotek: mění ji i obnova z koše a smazání z prohlížeče.
  const invalidateSources = () => queryClient.invalidateQueries({ queryKey: MEDIA_SOURCES_KEY });
  const refreshAfterBulk = () => {
    void queryClient.invalidateQueries({ queryKey: MEDIA_LIST_KEY });
    void queryClient.invalidateQueries({ queryKey: ["media-folders"] });
    void invalidateSources();
  };

  const restore = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/upload/${id}/restore`, {}),
    onSuccess: () => {
      void invalidateList();
      void invalidateSources();
    },
    // Kód z registru (`media_restore_live_twin`) už klient přeložil do textu slovníku.
    onError: (err: Error) => toast.error(err.message),
  });
  const restoringId = restore.isPending ? (restore.variables ?? null) : null;

  const onIntent = (id: string, intent: TileClickIntent) => {
    if (intent === "toggle") return selection.toggle(id);
    if (intent === "extend") {
      return selection.extendTo(
        id,
        items.map((m) => m.id),
      );
    }
    if (trashMode) return setDetail(id);
    const index = items.findIndex((m) => m.id === id);
    if (index !== -1) setViewerIndex(index);
  };

  const onViewerDeleted = (id: string) => {
    const remaining = items.filter((m) => m.id !== id).length;
    library.removeItem(id);
    void invalidateSources();
    setViewerIndex((i) => (i === null || remaining === 0 ? null : Math.min(i, remaining - 1)));
  };

  return (
    <div className="space-y-4">
      {/* Hlavička */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("media.page.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">{t("media.page.subtitle")}</p>
        </div>
        <div className="flex gap-2 self-start">
          <button
            type="button"
            data-media-merge-open
            onClick={() => setMergeOpen(true)}
            className="btn-secondary"
          >
            <Layers className="h-5 w-5" />
            {t("media.page.findDuplicates")}
          </button>
          <button type="button" onClick={() => setUploadOpen((v) => !v)} className="btn-primary">
            <Upload className="h-5 w-5" />
            {t("common.actions.upload")}
            {uploadOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Oblast nahrávání: vykreslená bez podmínky, dávka a hlídání odchodu přežijí schování. */}
      <MediaUploadArea
        open={uploadOpen}
        country={filters.country}
        onClose={() => setUploadOpen(false)}
        onSettled={refreshAfterBulk}
      />

      <div className="flex flex-col gap-4 sm:flex-row">
        {/* Levý sloupec: alba a druh */}
        <aside className="shrink-0 space-y-3 sm:w-56">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
            {t("media.albums.title")}
          </h2>
          <AlbumTree
            manage
            selectedId={filters.folderId ?? null}
            onSelect={(id) => setFilter("folderId", id)}
          />
          <MediaFolderFilter value={filters.folder} onChange={(f) => setFilter("folder", f)} />
        </aside>

        <div className="min-w-0 flex-1 space-y-4">
          <MediaFilters
            filters={filters}
            onChange={setFilter}
            onClear={clearFilters}
            activeCount={activeCount}
            showClear={activeCount > 0 && grid.state !== "empty"}
            sources={sources.data ?? []}
          />

          {selection.selected.size > 0 && (
            <MediaBulkBar
              selection={selection}
              items={items}
              trashMode={trashMode}
              onDone={refreshAfterBulk}
            />
          )}

          <MediaGrid
            library={library}
            grid={grid}
            selectedIds={selection.selected}
            trashMode={trashMode}
            restoringId={restoringId}
            hasActiveFilters={activeCount > 0}
            onIntent={onIntent}
            onRestore={(id) => restore.mutate(id)}
            onClearFilters={clearFilters}
            onSelectLoaded={selection.selectAll}
          />
        </div>
      </div>

      {/* Prohlížeč; detail stojí v JSX za ním, takže při shodném z-50 je nahoře. */}
      {viewerIndex !== null && items[viewerIndex] && (
        <PhotoViewer
          items={items}
          index={viewerIndex}
          total={library.total ?? items.length}
          hasMore={library.hasNextPage}
          onIndexChange={setViewerIndex}
          // Chyba donačtení slib odmítne, prohlížeč ji ukáže a index nechá.
          onLoadMore={async () => {
            await library.fetchNextPage({ throwOnError: true });
          }}
          onClose={() => setViewerIndex(null)}
          onOpenDetail={(id) => setDetail(id)}
          onChanged={library.patchItem}
          onDeleted={onViewerDeleted}
          mode="library"
        />
      )}

      {detailId && (
        <MediaDetailDrawer
          id={detailId}
          inTrash={trashMode}
          onClose={() => setDetail(null)}
          onChanged={() => {
            void invalidateList();
            void invalidateSources();
          }}
        />
      )}

      {/* Sloučení duplicit (náhled → potvrzení po dávkách) */}
      {mergeOpen && (
        <MergeDuplicatesDialog
          onClose={(changed) => {
            setMergeOpen(false);
            // Sloučení mění seznam, alba i zdroj kanonické: tytéž klíče jako hromadná akce.
            if (changed) refreshAfterBulk();
          }}
        />
      )}
    </div>
  );
}
